import {A11Y_EXCLUDE_INFO_BLOCK, unitTypeKeys} from "../../../constants/constants";
import {FC, useEffect} from "react";
import styled from "styled-components";
import {A11yStandardType} from "../../../models/A11yStandard";
import {useTranslation} from "react-i18next";
import {AccessibilityUnitLevel} from "../../../models/AccessibilityLevel";
import {AccessibilityUnit} from "../../../models/AccessibilityUnits";
import {Property} from "../../../models/Property";
import {getAccessibilityUnitLevelNumber} from "../../../utils/accessibilities";
import FloorsSvg from "../../../assets/svg/FloorsSvg";
import AccessibleParkingSvg from "../../../assets/svg/AccessibleParkingSvg";
import AccessibilityToiletSvg from "../../../assets/svg/AccessibilityToiletSvg";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AccessibleRoomSvg from "../../../assets/svg/AccessibleRoomSvg";
import AttractionSvg from "../../../assets/svg/AttractionSvg";
import {getPropertyKpis, PropertyKpi} from "../../../constants/kpi";
import {EMPTY_KPI_CELL, hasKpiData, KpiCell, sortKpiCells} from "../../../components/kpi/kpiCells";
import {KpiGrid} from "../../../components/kpi/KpiGrid";
import {PropertyPageTabKey, SHOPS_TAB} from "../../../constants/propertyPageTabs";

const StyledDiv = styled.div`
  margin-top: -9px !important;
`;

const numberOrZero = (value: string | number | undefined) => (value ? Number(value) : 0)

type PropsType = {
  a11yStandard: A11yStandardType
  property: Property
  totalShop: number | undefined
  parkingSpaces:  AccessibilityUnitLevel[] | undefined
  toiletRoom: AccessibilityUnitLevel[] | undefined,
  onGetStatusData: (status: boolean) => void,
  units: AccessibilityUnit[],
  onSetTab: (tab: PropertyPageTabKey) => void
}

const InformationBlock: FC<PropsType> = ({
  a11yStandard,
  property,
  totalShop,
  parkingSpaces,
  toiletRoom,
  onGetStatusData,
  units,
  onSetTab
}) => {
  const { t } = useTranslation()
  const info = property.additional_information

  const cells: Record<PropertyKpi, KpiCell> = {
    floors: {
      title: t("hotel_floors"),
      value: numberOrZero(property.amount_of_floor_levels),
      icon: <FloorsSvg stroke={'#636363'} height={30} width={30}/>,
    },
    totalRooms: {
      title: t("hotel_totalRooms"),
      value: numberOrZero(info.property_rooms),
    },
    accessibleRooms: {
      title: t("hotel_accessibleRooms"),
      value: numberOrZero(info.disabled_rooms),
      icon: <AccessibleRoomSvg fill={'#636363'} height={25} width={25}/>,
      onClick: () => onSetTab(unitTypeKeys.hotelRoomType),
    },
    allergyRooms: {
      title: t("hotel_allergyRooms"),
      value: numberOrZero(info.allergy_rooms),
    },
    parking: {
      title: t("shopping_mall_accessibleParking"),
      value: parkingSpaces ? getAccessibilityUnitLevelNumber(parkingSpaces, units) : undefined,
      icon: <AccessibleParkingSvg fill={'#636363'} height={23} width={23}/>,
    },
    toilets: {
      title: t('amount_of_toilets'),
      value: toiletRoom ? toiletRoom.length : undefined,
      icon: <AccessibilityToiletSvg fill={'#636363'}/>,
      onClick: () => onSetTab(unitTypeKeys.toiletRoomCommon),
    },
    accessibleToilets: {
      title: t('amount_of_accessible_toilets'),
      value: toiletRoom ? getAccessibilityUnitLevelNumber(toiletRoom, units) : undefined,
      icon: <AccessibilityToiletSvg fill={'#636363'}/>,
      onClick: () => onSetTab(unitTypeKeys.toiletRoomCommon),
    },
    shops: {
      title: t("shopping_mall_totalShops"),
      value: totalShop,
      icon: <ShoppingBasketIcon fill={'#636363'}/>,
      onClick: () => onSetTab(SHOPS_TAB),
    },
    attractions: {
      title: t("amount_of_attractions"),
      value: units.filter((unit) => unit.type === unitTypeKeys.attraction).length,
      icon: <AttractionSvg stroke={'#636363'} height={30} width={30}/>,
    },
    empty: EMPTY_KPI_CELL,
  }

  const shownCells = sortKpiCells(getPropertyKpis(a11yStandard.key).map((kpi) => cells[kpi]))
  const hasCells = shownCells.length > 0
  const hasData = hasKpiData(shownCells)

  useEffect(() => {
    // tells the page whether this block takes up space in the widget
    if (hasCells) {
      onGetStatusData(hasData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCells, hasData]);

  if (Object.values(A11Y_EXCLUDE_INFO_BLOCK).includes(a11yStandard.key)) {
    return <StyledDiv/>
  }

  return <KpiGrid cells={shownCells}/>
}

export default InformationBlock
